import { useState } from "react";
import { Navigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  addToGroup,
  can,
  createGroup,
  deleteGroup,
  listGroups,
  removeFromGroup,
  renameGroup,
  type Group,
} from "../api";
import BackLink from "../components/BackLink";
import ConfirmDelete from "../components/ConfirmDelete";
import MutationError from "../components/MutationError";
import { useT } from "../i18n";
import { primaryOrg, useSession } from "../session";

/**
 * Groups is the library's lists of people that a published book can be shared
 * with (ADR-0153, requirement 0083).
 *
 * A group owns nothing and spends nothing: it is who may read the books its
 * library shares with it. Which books those are is chosen on each book's
 * Publish page, not here — this page is only the people.
 */
export default function Groups() {
  const t = useT();
  const { me, isPending: sessionPending } = useSession();
  const org = primaryOrg(me);
  const client = useQueryClient();
  const [name, setName] = useState("");

  const groups = useQuery({
    queryKey: ["groups", org?.slug],
    queryFn: () => listGroups(org!.slug),
    enabled: !!org && can(org, "group.manage"),
  });
  const create = useMutation({
    mutationFn: () => createGroup(org!.slug, name.trim()),
    onSuccess: () => {
      setName("");
      void client.invalidateQueries({ queryKey: ["groups"] });
    },
  });

  if (sessionPending) return <main className="mx-auto max-w-3xl px-4 py-10" aria-busy="true" />;
  if (!me) return <Navigate to="/" replace />;

  return (
    <main className="mx-auto max-w-3xl px-4 py-10">
      <BackLink to="/studio" testID="groups-back">
        {t.studio.title}
      </BackLink>
      <h1 className="mt-4 text-2xl font-semibold tracking-tight">{t.groups.title}</h1>
      <p className="mt-1 mb-6 text-sm text-muted">{t.groups.intro}</p>

      {!can(org, "group.manage") ? (
        <p data-testid="groups-forbidden" className="text-sm text-muted">
          {t.groups.forbidden}
        </p>
      ) : (
        <>
          <form
            className="mb-6 flex gap-2"
            onSubmit={(e) => {
              e.preventDefault();
              if (name.trim()) create.mutate();
            }}
          >
            <input
              dir="auto"
              value={name}
              maxLength={80}
              onChange={(e) => setName(e.target.value)}
              placeholder={t.groups.newGroupName}
              aria-label={t.groups.newGroupName}
              data-testid="new-group-name"
              className="min-w-0 flex-1 rounded-control border border-line bg-surface px-3 py-2 text-sm"
            />
            <button
              type="submit"
              data-testid="create-group"
              disabled={!name.trim() || create.isPending}
              className="rounded-control bg-sage px-4 py-2 text-sm font-medium text-on-sage transition-colors hover:bg-sage-deep disabled:opacity-40"
            >
              {t.groups.create}
            </button>
          </form>
          <MutationError error={create.error} fallback={t.groups.failed} />

          {groups.isPending && <div aria-busy="true" className="h-20" />}
          {groups.isError && (
            <p role="alert" className="text-sm text-red-600 dark:text-red-400">
              {t.groups.failed}
            </p>
          )}
          {groups.data?.items.length === 0 && (
            <p
              data-testid="groups-empty"
              className="rounded-card border border-dashed border-line px-6 py-10 text-center text-sm text-muted"
            >
              {t.groups.empty}
            </p>
          )}
          <ul className="space-y-4" data-testid="group-list">
            {groups.data?.items.map((g) => (
              <GroupCard key={g.id} group={g} />
            ))}
          </ul>
        </>
      )}
    </main>
  );
}

function GroupCard({ group }: { group: Group }) {
  const t = useT();
  const client = useQueryClient();
  const [email, setEmail] = useState("");
  const [renaming, setRenaming] = useState(false);
  const [newName, setNewName] = useState(group.name);

  const refresh = () => client.invalidateQueries({ queryKey: ["groups"] });
  const add = useMutation({
    mutationFn: () => addToGroup(group.id, email.trim()),
    onSuccess: () => {
      setEmail("");
      void refresh();
    },
  });
  const remove = useMutation({
    mutationFn: (userID: string) => removeFromGroup(group.id, userID),
    onSuccess: () => void refresh(),
  });
  const rename = useMutation({
    mutationFn: () => renameGroup(group.id, newName.trim()),
    onSuccess: () => {
      setRenaming(false);
      void refresh();
    },
  });
  const destroy = useMutation({
    mutationFn: () => deleteGroup(group.id),
    onSuccess: () => {
      void refresh();
      // A book shared with it has lost these readers.
      void client.invalidateQueries({ queryKey: ["book"] });
    },
  });

  return (
    <li data-testid="group" className="rounded-card border border-line bg-surface p-4">
      <div className="flex flex-wrap items-center gap-2">
        {renaming ? (
          <form
            className="flex min-w-0 flex-1 gap-2"
            onSubmit={(e) => {
              e.preventDefault();
              if (newName.trim()) rename.mutate();
            }}
          >
            <input
              dir="auto"
              value={newName}
              maxLength={80}
              onChange={(e) => setNewName(e.target.value)}
              aria-label={t.groups.newGroupName}
              data-testid="rename-group-name"
              className="min-w-0 flex-1 rounded-control border border-line bg-surface px-3 py-1.5 text-sm"
            />
            <button
              type="submit"
              disabled={!newName.trim() || rename.isPending}
              className="rounded-control border border-line px-3 py-1.5 text-xs hover:bg-sunk disabled:opacity-40"
            >
              {t.groups.save}
            </button>
            <button
              type="button"
              onClick={() => setRenaming(false)}
              className="rounded-control px-3 py-1.5 text-xs text-muted hover:bg-sunk"
            >
              {t.groups.cancel}
            </button>
          </form>
        ) : (
          <>
            <h2 dir="auto" data-testid="group-name" className="min-w-0 flex-1 truncate font-medium">
              {group.name}
            </h2>
            <span className="text-xs text-muted">{t.groups.people(group.people.length)}</span>
            <button
              type="button"
              data-testid="rename-group"
              onClick={() => {
                setNewName(group.name);
                setRenaming(true);
              }}
              className="rounded-control border border-line px-3 py-1.5 text-xs hover:bg-sunk"
            >
              {t.groups.rename}
            </button>
            <ConfirmDelete
              label={t.groups.delete}
              question={t.groups.confirmDelete(group.name)}
              onConfirm={() => destroy.mutate()}
              testId="delete-group"
              disabled={destroy.isPending}
            />
          </>
        )}
      </div>
      <MutationError error={rename.error ?? destroy.error} fallback={t.groups.failed} />

      {group.people.length === 0 ? (
        <p className="mt-3 text-xs text-muted">{t.groups.nobody}</p>
      ) : (
        <ul className="mt-3 divide-y divide-line" data-testid="group-people">
          {group.people.map((p) => (
            <li key={p.user_id} data-testid="group-person" className="flex items-center gap-3 py-2">
              <div className="min-w-0 flex-1">
                {p.name && (
                  <p dir="auto" className="truncate text-sm">
                    {p.name}
                  </p>
                )}
                <p className="truncate text-xs text-muted">{p.email}</p>
              </div>
              <button
                type="button"
                data-testid="remove-person"
                disabled={remove.isPending}
                onClick={() => remove.mutate(p.user_id)}
                className="rounded-control border border-line px-3 py-1.5 text-xs hover:bg-sunk disabled:opacity-40"
              >
                {t.groups.remove}
              </button>
            </li>
          ))}
        </ul>
      )}

      <form
        className="mt-3 flex gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          if (email.trim()) add.mutate();
        }}
      >
        <input
          type="email"
          dir="ltr"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder={t.groups.addPersonEmail}
          aria-label={t.groups.addPersonEmail}
          data-testid="add-person-email"
          className="min-w-0 flex-1 rounded-control border border-line bg-surface px-3 py-2 text-sm"
        />
        <button
          type="submit"
          data-testid="add-person"
          disabled={!email.trim() || add.isPending}
          className="rounded-control border border-line px-3 py-2 text-sm hover:bg-sunk disabled:opacity-40"
        >
          {t.groups.addPerson}
        </button>
      </form>
      <MutationError error={add.error ?? remove.error} fallback={t.groups.failed} />
    </li>
  );
}
